// CB-CONV §3.4 rule 2: percentages and tax use rational arithmetic, never floating point.
// 10% is 1/10, not 0.1. Every Rational is kept normalised: denominator positive, gcd 1.
function gcd(a, b) {
    let x = a < 0n ? -a : a;
    let y = b < 0n ? -b : b;
    while (y !== 0n) {
        [x, y] = [y, x % y];
    }
    return x;
}
export class Rational {
    numerator;
    denominator;
    constructor(numerator, denominator) {
        this.numerator = numerator;
        this.denominator = denominator;
    }
    static of(numerator, denominator = 1n) {
        if (denominator === 0n) {
            throw new RangeError('Rational denominator must not be zero');
        }
        const sign = denominator < 0n ? -1n : 1n;
        const n = numerator * sign;
        const d = denominator * sign;
        const g = gcd(n, d);
        return g === 1n || g === 0n ? new Rational(n, d) : new Rational(n / g, d / g);
    }
    static fromInt(value) {
        if (typeof value === 'number' && !Number.isSafeInteger(value)) {
            throw new RangeError(`Rational.fromInt requires a safe integer, got ${value}`);
        }
        return Rational.of(BigInt(value));
    }
    /** `percent(10)` is 1/10. Accepts integer percentages only; use `of` for anything finer. */
    static percent(value) {
        return Rational.of(BigInt(value), 100n);
    }
    static ZERO = Rational.of(0n);
    static ONE = Rational.of(1n);
    add(other) {
        return Rational.of(this.numerator * other.denominator + other.numerator * this.denominator, this.denominator * other.denominator);
    }
    subtract(other) {
        return this.add(other.negate());
    }
    multiply(other) {
        return Rational.of(this.numerator * other.numerator, this.denominator * other.denominator);
    }
    divide(other) {
        if (other.numerator === 0n) {
            throw new RangeError('Division by zero');
        }
        return Rational.of(this.numerator * other.denominator, this.denominator * other.numerator);
    }
    negate() {
        return Rational.of(-this.numerator, this.denominator);
    }
    isZero() {
        return this.numerator === 0n;
    }
    isNegative() {
        return this.numerator < 0n;
    }
    compareTo(other) {
        const left = this.numerator * other.denominator;
        const right = other.numerator * this.denominator;
        return left < right ? -1 : left > right ? 1 : 0;
    }
    equals(other) {
        return this.numerator === other.numerator && this.denominator === other.denominator;
    }
    /** Integer part toward negative infinity, and the non-negative remainder as a Rational in [0, 1). */
    floorDivMod() {
        let floor = this.numerator / this.denominator;
        let rem = this.numerator % this.denominator;
        if (rem < 0n) {
            floor -= 1n;
            rem += this.denominator;
        }
        return { floor, remainder: Rational.of(rem, this.denominator) };
    }
    toString() {
        return this.denominator === 1n
            ? this.numerator.toString()
            : `${this.numerator}/${this.denominator}`;
    }
}
//# sourceMappingURL=rational.js.map